import { hasWindow } from "@omni/utils/dom.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Messages } from "@/components/General/Messages/messages.container";
import { AddToCartModal } from "@/components/Modals/AddToCartModal/addToCartModal.container";
import { useThunkDispatch } from "@/redux/dispatcher";
import {
    CartItemModel,
    CartModel
} from "@/redux/modules/api/data/cart/@types/cart.model";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useModalRouter } from "@/utils/hooks/useModalRouter";

import CreateGiftCardComponent from "./createGiftCard.component";
import { GiftCard } from "./partials/giftCardType.schema";

const getCartId = () => new URLSearchParams(location.search).get("cartid");

export type CreateGiftCardContainerProps = {
    giftCards: Array<GiftCard>;
};

export const CreateGiftCardContainer = ({
    giftCards
}: CreateGiftCardContainerProps) => {
    const { t } = useGranite();

    const isAddingGiftCard = useSelector(CartSelectors.isLoading);

    const isCardLoaded = useSelector(CartSelectors.isCartSuccessfullyLoaded);
    const shoppingCartError = useSelector(CartSelectors.getCartError);
    const shoppingCart = useSelector(CartSelectors.getCart);
    const dispatch = useThunkDispatch();

    const [addToCartProduct, setAddToCartProduct] =
        useState<CartItemModel | null>(null);

    const { open: openModal } = useModalRouter(
        ModalType.AddToCart,
        addToCartProduct?.sku
    );

    const [cartItemId, setCartItemId] = useState<string | null>(null);

    const cartProduct = shoppingCart?.items.find((i) => i.id === cartItemId);

    useEffect(() => {
        const urlCartItemId = getCartId();
        if (!isDefined(urlCartItemId)) return;

        setCartItemId(urlCartItemId);

        dispatch(CartModule.load());
    }, [dispatch]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (cartItemId && !cartProduct && isCardLoaded) {
            broadcastMessage({
                id: "update-giftcard-error",
                text: t("giftcard.update.failure.message"),
                status: "danger",
                dismissible: true,
                activeScroll: true
            });
        } else {
            clearMessageById("update-giftcard-error");
        }
    }, [cartItemId, cartProduct, isCardLoaded]);

    useEffect(() => {
        if (addToCartProduct) {
            openModal(false);
        }
    }, [addToCartProduct, openModal]);

    const onSubmit = (request: CartModule.GiftCardRequest): Promise<any> => {
        clearMessageById("create-giftcard-failure");
        clearMessageById("update-giftcard-error");
        setAddToCartProduct(null);

        const isNewCreateGiftCard = !cartItemId;

        const makeCall = () => {
            if (!isNewCreateGiftCard) {
                return dispatch(
                    CartModule.updateGiftCardInCart(cartItemId, request)
                );
            } else {
                return dispatch(CartModule.addGiftCardToCart(request));
            }
        };

        return new Promise<void>((resolve, reject) => {
            makeCall().then(({ type, payload }) => {
                if (type === CartModule.APIStatus.SUCCESS) {
                    resolve();
                    const cart: CartModel = payload;
                    const cartProduct = cart.items.find(
                        (i) => i.sku === request.sku
                    );
                    if (!isNewCreateGiftCard) {
                        const url = new URL(location.href);
                        url.searchParams.delete("cartid");
                        window.history.replaceState(
                            null,
                            "",
                            url.pathname + url.search
                        );
                    }

                    setAddToCartProduct(cartProduct ?? null);
                } else {
                    broadcastMessage({
                        id: "create-giftcard-failure",
                        text: t("giftcard.create.failure.message"),
                        status: "danger",
                        dismissible: true,
                        activeScroll: true
                    });
                    reject();
                }
            });
        });
    };

    const onModalClose = () => {
        setAddToCartProduct(null);
    };

    const isCartLoading =
        isDefined(cartItemId) && !shoppingCartError && !isCardLoaded;

    const isLoading = !hasWindow() || isAddingGiftCard || isCartLoading;

    return (
        <>
            <Messages messageid={"update-giftcard-error"} />
            <CreateGiftCardComponent
                key={cartProduct?.sku}
                onSubmit={onSubmit}
                isLoading={isLoading}
                cards={giftCards}
                cartProduct={cartProduct}
            />
            {addToCartProduct && (
                <AddToCartModal
                    sku={addToCartProduct.sku}
                    title={addToCartProduct.title}
                    medias={addToCartProduct.medias}
                    salesIncentives={[]}
                    visibilityType={addToCartProduct.productVisibilityType}
                    boxArticle={addToCartProduct.boxArticle}
                    additionalService={addToCartProduct.isAdditionalService}
                    currentPrice={addToCartProduct.totalPrice}
                    strikethroughPrice={addToCartProduct.strikethroughPrice}
                    promotions={[]}
                    onClose={onModalClose}
                />
            )}
        </>
    );
};
